import {ReactNode} from 'react';
import {Link} from 'react-router-dom';
import Icon from '../Icon';
import {Species, varietiesLabel} from '../../lib/plants';
import {PlantSummary} from '../../types';

// One plant in the Plants list: the most recent photo (or a leaf tile), name · variety, a
// one-line subtitle, and a chevron. The same geometry as TellRow. A species with several
// varieties gets the same row, saying how many, and opens the species page.

function Row({to, thumbUrl, permanent, title, subtitle}: {to: string; thumbUrl: string | null; permanent: boolean; title: ReactNode; subtitle: string}) {
    return (
        <Link to={to} className="flex min-h-[64px] items-center gap-3 py-2 no-underline">
            {thumbUrl ? (
                <img src={thumbUrl} alt="" loading="lazy" className="h-12 w-12 shrink-0 rounded-[12px] object-cover" />
            ) : (
                <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-[12px] bg-kind-grow-bg text-kind-grow-fg">
                    <Icon name={permanent ? 'tree' : 'leaf'} size={24} />
                </span>
            )}
            <span className="min-w-0 flex-1">
                <span className="block truncate">{title}</span>
                {subtitle && <span className="meta block truncate">{subtitle}</span>}
            </span>
            <Icon name="chevron" size={20} className="shrink-0 text-ink-3" />
        </Link>
    );
}

export default function PlantRow({plant, subtitle, title}: {plant: PlantSummary; subtitle: string; title?: string}) {
    return (
        <Row
            to={`/plants/${plant.id}`}
            thumbUrl={plant.thumbUrl}
            permanent={plant.isPermanent}
            subtitle={subtitle}
            title={
                title !== undefined ? (
                    <span className="font-semibold">{title}</span>
                ) : (
                    <>
                        <span className="font-semibold">{plant.name}</span>
                        {plant.variety && <span className="text-ink-2"> · {plant.variety}</span>}
                    </>
                )
            }
        />
    );
}

/** A name with several varieties; a name with one is just its PlantRow. */
export function SpeciesRow({species, subtitle}: {species: Species; subtitle: string}) {
    return (
        <Row
            to={`/plants/species/${encodeURIComponent(species.key)}`}
            thumbUrl={species.thumbUrl}
            permanent={species.group === 'permanent'}
            subtitle={subtitle}
            title={
                <>
                    <span className="font-semibold">{species.name}</span>
                    <span className="text-ink-2"> · {varietiesLabel(species.plants.length)}</span>
                </>
            }
        />
    );
}
